/**
 * Promo popup behaviour (see sections/la-popup.liquid).
 *
 * Opens a native <dialog> via showModal(), which gives the focus trap, the
 * inert background and Escape-to-close for free — no bespoke focus management.
 *
 * Triggers (delay / scroll depth / exit intent) are all merchant-configurable and
 * race each other: whichever fires first opens the popup and every listener is torn
 * down, so it can never open twice in one visit.
 *
 * Suppression is stored per campaign in localStorage. Changing the campaign name in
 * the theme editor changes the key, which re-shows a NEW promo to people who
 * dismissed the previous one — without that, a fresh campaign would stay invisible
 * to exactly the repeat visitors it is aimed at.
 *
 * Signup sends the address to the ActiveCampaign form (assets/la-ac.js), whose
 * automation emails the code, and posts the native customer form over fetch in
 * parallel so the Shopify list grows too. The shopper stays on the page.
 * Shopify answers a successful customer signup by redirecting to the same page with
 * `customer_posted=true`, which is what we test for.
 *
 * If that fetch is refused — Shopify layers hCaptcha spam protection over customer
 * forms, and a background POST can be rejected where a real form submission would
 * pass — we do not just show an error and lose the signup. We record a pending flag
 * and let the form submit natively, so Shopify's own captcha flow runs. On the way
 * back, the flag plus `customer_posted=true` reopens the popup straight onto the
 * reward panel. The flag is what keeps this honest: without it, ANY customer form on
 * the page (the footer newsletter included) would pop this modal open, because
 * Shopify reports posted-successfully page-wide rather than per form.
 */
(function () {
  var root = document.querySelector('[data-la-popup]');
  if (!root || root.dataset.lapInit) return;
  root.dataset.lapInit = '1';

  var DAY = 24 * 60 * 60 * 1000;
  var campaign = root.dataset.campaign || 'default';
  var storageKey = 'la-popup:' + campaign;
  var pendingKey = 'la-popup-pending:' + campaign;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  var capture = root.querySelector('[data-lap-panel="capture"]');
  var reward = root.querySelector('[data-lap-panel="reward"]');
  var form = root.querySelector('#lap-form');
  var submit = root.querySelector('[data-lap-submit]');
  var errorEl = root.querySelector('[data-lap-error]');

  // localStorage throws outright in some privacy modes; a popup is never worth an
  // exception, so every access degrades to "no record kept".
  function snoozedUntil() {
    try {
      return Number(window.localStorage.getItem(storageKey)) || 0;
    } catch (e) {
      return 0;
    }
  }

  function setPending(on) {
    try {
      if (on) window.localStorage.setItem(pendingKey, '1');
      else window.localStorage.removeItem(pendingKey);
    } catch (e) {
      /* private mode */
    }
  }

  function isPending() {
    try {
      return window.localStorage.getItem(pendingKey) === '1';
    } catch (e) {
      return false;
    }
  }

  function snooze(days) {
    try {
      window.localStorage.setItem(storageKey, String(Date.now() + days * DAY));
    } catch (e) {
      /* private mode — the popup simply may reappear next visit */
    }
  }

  var retry = null;

  function open() {
    if (root.open) return;

    // Never stack on top of the cart drawer, the search modal or a quick-add: the
    // shopper is mid-task, and two modal dialogs at once trap focus in the wrong one.
    // Wait for the other one to close rather than burning the trigger — a delay that
    // happened to elapse while the drawer was open would otherwise lose the popup for
    // the whole pageview, and the drawer reopens on its own after any add-to-cart.
    if (document.querySelector('dialog[open]')) {
      if (!retry) retry = setInterval(function () {
        if (!document.querySelector('dialog[open]')) open();
      }, 1000);
      return;
    }

    teardown();
    try {
      root.showModal();
    } catch (e) {
      /* older engine without showModal — leave the page alone */
    }
  }

  // Close plays the section's exit animation first (.lap--closing) and only then
  // calls the native close(), so the card and backdrop leave together instead of
  // vanishing on the next frame. Reduced-motion users get the immediate close.
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var closing = false;

  function close() {
    if (!root.open || closing) return;
    if (reducedMotion.matches) {
      root.close();
      return;
    }
    closing = true;
    root.classList.add('lap--closing');
    var fallback = setTimeout(finish, 400);
    function onEnd(e) {
      // Child animations (the staggered copy) bubble their animationend up here;
      // only the dialog's own exit marks the end.
      if (e.target !== root) return;
      finish();
    }
    function finish() {
      clearTimeout(fallback);
      root.removeEventListener('animationend', onEnd);
      root.classList.remove('lap--closing');
      closing = false;
      if (root.open) root.close();
    }
    root.addEventListener('animationend', onEnd);
  }

  // Escape fires `cancel`, which would close instantly; route it through the same
  // animated close. A second Escape while closing is ignored by the guard above.
  root.addEventListener('cancel', function (e) {
    e.preventDefault();
    close();
  });

  /* ---------- triggers ---------- */

  var timer = null;
  var listening = false;

  function onScroll() {
    var max = document.documentElement.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    var pct = (window.scrollY / max) * 100;
    if (pct >= Number(root.dataset.scroll)) open();
  }

  function onExit(e) {
    // Only a real pointer leaving through the top of the viewport counts; a phone
    // has no equivalent gesture, and matching on any mouseleave fires constantly.
    if (e.clientY <= 0) open();
  }

  function teardown() {
    if (timer) clearTimeout(timer);
    if (retry) {
      clearInterval(retry);
      retry = null;
    }
    if (!listening) return;
    listening = false;
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseleave', onExit);
  }

  function arm() {
    var delay = Number(root.dataset.delay) || 0;
    var scroll = Number(root.dataset.scroll) || 0;
    var exit = root.dataset.exit === 'true';
    var finePointer = window.matchMedia('(pointer: fine)').matches;

    if (delay > 0) timer = setTimeout(open, delay * 1000);
    if (scroll > 0 || (exit && finePointer)) {
      listening = true;
      if (scroll > 0) window.addEventListener('scroll', onScroll, { passive: true });
      if (exit && finePointer) document.addEventListener('mouseleave', onExit);
    }
  }

  /* ---------- close / decline ---------- */

  root.querySelectorAll('[data-lap-close]').forEach(function (el) {
    el.addEventListener('click', close);
  });

  // Clicking the backdrop closes it. A <dialog> reports the click as landing on the
  // dialog itself, so compare against the element's own box to tell the two apart.
  root.addEventListener('click', function (e) {
    if (e.target !== root) return;
    var r = root.getBoundingClientRect();
    var inside =
      e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) close();
  });

  // Fires for the close button, the decline link, the backdrop and Escape alike.
  root.addEventListener('close', function () {
    if (designMode) return;
    // Someone who reached the reward panel has already signed up; hold them to the
    // longer signup window rather than resetting them to the dismissal one.
    var signedUp = reward && !reward.hidden;
    snooze(Number(signedUp ? root.dataset.snoozeSignup : root.dataset.snoozeDismiss) || 14);
  });

  /* ---------- signup ---------- */

  function showReward() {
    setPending(false);
    if (capture) capture.hidden = true;
    if (!reward) {
      close();
      return;
    }
    reward.hidden = false;
    // Move focus to the reward heading so a screen reader announces the new panel
    // instead of leaving focus on a submit button that no longer exists visually.
    var h = reward.querySelector('.lap__title');
    if (h) {
      h.setAttribute('tabindex', '-1');
      h.focus();
    }
    snooze(Number(root.dataset.snoozeSignup) || 365);
  }

  if (form) {
    var acAccount = (root.getAttribute('data-ac-account') || '').trim();
    var acForm = (root.getAttribute('data-ac-form') || '').trim();
    var emailInput = form.querySelector('input[type="email"]');

    // Shopify customer list. Resolves true on a recorded signup, false when the
    // background POST is refused (almost always hCaptcha spam protection).
    function sendToShopify() {
      return fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      }).then(function (r) {
        // Success is a redirect back carrying customer_posted=true; a rejected
        // address re-renders in place.
        return !!(r.url && r.url.indexOf('customer_posted=true') !== -1);
      });
    }

    function sendToAC(email) {
      if (!acAccount || !acForm || !window.LAActiveCampaign) {
        return Promise.reject({ fromAC: false });
      }
      return window.LAActiveCampaign.subscribe(acAccount, acForm, email);
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (errorEl) errorEl.hidden = true;

      var label = submit ? submit.textContent : '';
      if (submit) {
        submit.disabled = true;
        submit.textContent = 'Sending…';
      }
      function restore() {
        if (submit) {
          submit.disabled = false;
          submit.textContent = label;
        }
      }

      var email = emailInput ? emailInput.value.trim() : '';
      var tagsInput = form.querySelector('input[name="contact[tags]"]');

      // ActiveCampaign decides the outcome: its automation emails the code.
      sendToAC(email).then(
        function () {
          // Accepted. Shopify is best-effort list growth only, so the reward does
          // not wait on it, and it carries no ac-form tag (the automation has
          // already run; Flow must not resubmit).
          sendToShopify().catch(function () {});
          restore();
          showReward();
        },
        function (err) {
          if (err && err.fromAC) {
            // ActiveCampaign says the address itself is bad: let them fix it.
            restore();
            if (errorEl) errorEl.hidden = false;
            return;
          }
          // Blocked or unreachable. Tag the Shopify signup ac-form-<id> so the
          // Flow workflow submits the ActiveCampaign form on Shopify's side.
          if (tagsInput && acForm) {
            var base = tagsInput.value.split(',').map(function (t) { return t.trim(); })
              .filter(function (t) { return t && t.indexOf('ac-form-') !== 0; });
            base.push('ac-form-' + acForm);
            tagsInput.value = base.join(',');
          }
          sendToShopify().catch(function () { return false; }).then(function (ok) {
            if (ok) {
              restore();
              showReward();
              return;
            }
            // Shopify wants its captcha. Hand off to a real form submission (tags
            // included); the pending flag reopens the popup on the reward panel.
            setPending(true);
            HTMLFormElement.prototype.submit.call(form);
          });
        }
      );
    });
  }

  /* ---------- copy the code ---------- */

  var copyBtn = root.querySelector('[data-lap-copy]');
  if (copyBtn) {
    copyBtn.addEventListener('click', function () {
      var code = root.querySelector('[data-lap-code]');
      var out = root.querySelector('[data-lap-copied]');
      if (!code) return;
      var text = code.textContent.trim();

      function done() {
        if (out) {
          out.textContent = copyBtn.dataset.copiedLabel || 'Copied';
          setTimeout(function () {
            out.textContent = '';
          }, 3000);
        }
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, selectFallback);
      } else {
        selectFallback();
      }

      // Clipboard access is denied in some contexts; selecting the code at least
      // leaves it one keystroke from copied rather than failing silently.
      function selectFallback() {
        var sel = window.getSelection();
        var range = document.createRange();
        range.selectNodeContents(code);
        sel.removeAllRanges();
        sel.addRange(range);
        done();
      }
    });
  }

  /* ---------- boot ---------- */

  if (designMode) {
    // In the theme editor the popup follows selection instead of the triggers, so it
    // can be styled and proofread without waiting one out or clearing storage.
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target.contains(root) || e.target === root) open();
    });
    document.addEventListener('shopify:section:deselect', function () {
      close();
    });
    return;
  }

  /* ---------- open from a link ----------
   * Any link whose URL ends in #popup opens this popup while it is running, e.g. a
   * hero slide button. A deliberate click overrides the snooze: the shopper asked.
   * When the popup is off or outside its dates this script never loads, so the same
   * link just goes to its page; point it at a page that stands on its own.
   */
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href$="#popup"]');
    if (!link) return;
    e.preventDefault();
    open();
  });

  // Returning from a native submit that we handed off above: reopen straight onto the
  // reward panel rather than making the shopper hunt for the code they just earned.
  if (isPending() && window.location.search.indexOf('customer_posted=true') !== -1) {
    showReward();
    open();
    return;
  }
  setPending(false);

  if (Date.now() < snoozedUntil()) return;
  arm();
})();
